import React, { useState, useEffect, useMemo } from 'react';
import { getProductCompanyProfitReport, getUsers, getSales, getSalesQuantityReport } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Calendar, Users, Download, ChevronDown, ChevronUp, X, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/PageHeader';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { isCameraProduct } from '@/utils/productCategories';

const MONTHS = [
  { value: '01', label: 'Ocak' },
  { value: '02', label: 'Şubat' },
  { value: '03', label: 'Mart' },
  { value: '04', label: 'Nisan' },
  { value: '05', label: 'Mayıs' },
  { value: '06', label: 'Haziran' },
  { value: '07', label: 'Temmuz' },
  { value: '08', label: 'Ağustos' },
  { value: '09', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' }
];

// ==================== SATIŞ ADET RAPORLARI - ÜRÜN KATEGORİLERİ ====================
// ARVENTO PEŞİN SATIŞLAR
const ARVENTO_PESIN_PRODUCTS = [
  "Profesyonel Araç Takip Paketi (Bataryalı)",
  "112 Projesi – Profesyonel Paket",
  "Profesyonel Araç Takip Paketi (Bataryasız)",
  "Profesyonel Paket + SIM BAT",
  "Standart Araç Takip Paketi",
  "Standart Araç Takip Paketi Kiralama",
  "Standart Kolay Takip",
  "Yeşil Takip Projesi Hat Dahil Paket",
  "İMT.X55 Konteyner Paketi",
  "Treyki Asset Telematik Set (2 yıllık abonelik dahil)",
  "Treyki Moto Telematik Set (2 yıllık abonelik dahil)",
  "Treyki Yacht Telematik Set (2 yıllık abonelik dahil)",
];

// ARVENTO OPERATÖR SATIŞLARI
const ARVENTO_OPERATOR_PRODUCTS = [
  "Standart Araç Takip Paketi (Türk Telekom)",
  "Standart Araç Takip Paketi (Turkcell)",
  "Standart Araç Takip Paketi (Vodafone) – 12 Ay",
  "Standart Araç Takip Paketi (Vodafone) – 24 Ay",
  "Standart Araç Takip Paketi (Vodafone) 12 AY",
  "Standart Araç Takip Paketi (Vodafone) 24 AY",
];

// ARVENTO TAKSİTLİ SATIŞLAR
const ARVENTO_TAKSITLI_PRODUCTS = [
  "Bataryalı Profesyonel Araç Takip Paketi (Arvento Taksitli)",
  "Bataryasız Profesyonel Araç Takip Paketi (Arvento Taksitli)",
  "Standart Araç Takip Paketi (Arvento Taksitli)",
  "Profesyonel Araç Takip Paketi Bataryalı (Arvento Taksitli)",
  "Profesyonel Araç Takip Paketi Bataryasız (Arvento Taksitli)",
];

// Eski liste (geriye dönük uyumluluk)
const ARVENTO_PRODUCT_NAMES = [
  ...ARVENTO_PESIN_PRODUCTS,
  ...ARVENTO_OPERATOR_PRODUCTS,
  ...ARVENTO_TAKSITLI_PRODUCTS,
  "Motat Pro Paket", "Motat Projesi Motat SET", "İmt.14 Motat Proje Satışı", "Profesyonel Paket + SIM"
];

// Ürün adı normalize fonksiyonu
const normalizeProductName = (name) => {
  if (!name) return '';
  let normalized = name.trim().toLowerCase();
  normalized = normalized.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
  normalized = normalized.replace(/\s+/g, ' ');
  return normalized;
};

// Ürün kategorisini belirle (eski mantık)
const getProductCategory = (productName, category) => {
  if (!productName) return 'Diğer';
  
  const productNameNormalized = normalizeProductName(productName);
  
  // Arvento kontrolü
  const isArvento = ARVENTO_PRODUCT_NAMES.some(name => normalizeProductName(name) === productNameNormalized);
  if (isArvento) return 'Arvento';
  
  // SADECE 3 dashcam ürünü Kamera sayılır
  if (isCameraProduct(productName)) return 'Kamera';
  
  // Kategori bazlı kontrol
  if (category === 'M2M') return 'M2M';
  // Kamera Ürünleri kategorisindeki diğer ürünler ve Sensörler → Sensör
  if (category === 'Kamera Ürünleri' || category === 'Sensörler') return 'Sensör';
  
  return 'Diğer';
};

// Yeni rapor kategorisi belirleme fonksiyonu
const getReportCategory = (productName) => {
  if (!productName) return 'Diğer';
  const normalized = normalizeProductName(productName);
  
  if (ARVENTO_PESIN_PRODUCTS.some(p => normalizeProductName(p) === normalized)) return 'Arvento Peşin';
  if (ARVENTO_OPERATOR_PRODUCTS.some(p => normalizeProductName(p) === normalized)) return 'Arvento Operatör';
  if (ARVENTO_TAKSITLI_PRODUCTS.some(p => normalizeProductName(p) === normalized)) return 'Arvento Taksitli';
  
  return 'Diğer';
};

const ProductBasedReports = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [allSales, setAllSales] = useState([]);
  
  // Satış Adet Raporu state'leri
  const [salesQuantityData, setSalesQuantityData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Filtre state'leri
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(today.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(today.getFullYear()));
  const [selectedRep, setSelectedRep] = useState('all');
  
  // Özel tarih aralığı modu (Son 3 Ay, Tüm Zamanlar için)
  const [dateRangeMode, setDateRangeMode] = useState('month'); // 'month', 'last3months', 'alltime'
  
  // Sıralama state'i
  const [sortConfig, setSortConfig] = useState({ key: 'quantity', direction: 'desc' });
  
  // Detay dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState([]);
  
  // Yıl seçenekleri (2023-2030)
  const yearOptions = [];
  for (let y = 2023; y <= 2030; y++) {
    yearOptions.push(String(y));
  }
  
  useEffect(() => {
    loadInitialData();
  }, []);
  
  useEffect(() => {
    loadReportData();
    loadSalesQuantityData();
  }, [selectedMonth, selectedYear, selectedRep, dateRangeMode]);
  
  const loadInitialData = async () => {
    try {
      const [usersData, salesData] = await Promise.all([
        getUsers(),
        getSales()
      ]);
      
      // Sadece temsilci rolündekileri filtrele
      const reps = usersData.filter(u => u.role === 'temsilci' || u.role === 'abonelik');
      setUsers(reps);
      setAllSales(salesData);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    }
  };
  
  const loadSalesQuantityData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const data = await getSalesQuantityReport(startDate, endDate, selectedRep);
      setSalesQuantityData(data);
    } catch (error) {
      console.error('Satış adet verisi yüklenemedi:', error);
    }
  };
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Tarih aralığını hesapla
  const getDateRange = () => {
    if (dateRangeMode === 'last3months') {
      // Son 3 ay
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      const startDate = `${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`;
      const endDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      return { startDate, endDate };
    } else if (dateRangeMode === 'alltime') {
      // Tüm zamanlar (2020'den bugüne)
      return { startDate: '2020-01-01', endDate: `${today.getFullYear()}-12-31` };
    } else {
      // Normal ay filtresi
      const startDate = `${selectedYear}-${selectedMonth}-01`;
      const lastDay = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
      const endDate = `${selectedYear}-${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
      return { startDate, endDate };
    }
  };
  
  const loadReportData = async () => {
    try {
      setLoading(true);
      
      const { startDate, endDate } = getDateRange();
      const data = await getProductCompanyProfitReport(startDate, endDate, selectedRep);
      setProducts(data);
    } catch (error) {
      console.error('Rapor yüklenirken hata:', error);
      toast.error('Rapor yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  // Hızlı filtre: Bu Ay
  const handleThisMonth = () => {
    setDateRangeMode('month');
    setSelectedMonth(String(today.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(today.getFullYear()));
  };
  
  // Hızlı filtre: Geçen Ay
  const handleLastMonth = () => {
    setDateRangeMode('month');
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setSelectedMonth(String(lastMonth.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(lastMonth.getFullYear()));
  };
  
  // Hızlı filtre: Son 3 Ay
  const handleLast3Months = () => {
    setDateRangeMode('last3months');
  };
  
  // Hızlı filtre: Tüm Zamanlar
  const handleAllTime = () => {
    setDateRangeMode('alltime');
  };
  
  // Temizle
  const handleClear = () => {
    setDateRangeMode('month');
    setSelectedMonth(String(today.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(String(today.getFullYear()));
    setSelectedRep('all');
  };
  
  // Sıralama işlevi
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  // Sıralanmış ürünler
  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    sorted.sort((a, b) => {
      if (sortConfig.direction === 'asc') {
        return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
      }
      return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
    return sorted;
  }, [products, sortConfig]);
  
  // Toplam hesapla
  const totals = useMemo(() => {
    return products.reduce((acc, item) => ({
      quantity: acc.quantity + (item.quantity || 0),
      company_profit: acc.company_profit + (item.company_profit || 0)
    }), { quantity: 0, company_profit: 0 });
  }, [products]);
  
  // Kategori bazlı toplamları hesapla
  const categoryTotals = useMemo(() => {
    const totals = {
      arvento: 0,
      kamera: 0,
      m2m: 0,
      sensor: 0,
      diger: 0
    };
    
    products.forEach(item => {
      const productName = item.product_name || '';
      const productNameNormalized = normalizeProductName(productName);
      
      // Arvento kontrolü
      const isArvento = ARVENTO_PRODUCT_NAMES.some(name => normalizeProductName(name) === productNameNormalized);
      
      if (isArvento) {
        totals.arvento += item.quantity || 0;
      } else if (isCameraProduct(productName)) {
        // SADECE 3 dashcam Kamera sayılır
        totals.kamera += item.quantity || 0;
      } else if (productName.toLowerCase().includes('m2m') || item.category === 'M2M') {
        totals.m2m += item.quantity || 0;
      } else if (item.category === 'Kamera Ürünleri' || item.category === 'Sensörler' || productName.toLowerCase().includes('sensör')) {
        // Diğer Kamera Ürünleri ve tüm Sensörler → Sensör
        totals.sensor += item.quantity || 0;
      } else {
        totals.diger += item.quantity || 0;
      }
    });
    
    return totals;
  }, [products]);
  
  // Satıra tıklandığında detay göster
  const handleRowClick = async (product) => {
    setSelectedProduct(product);
    
    // Seçili ayın satışlarından bu ürüne ait olanları filtrele
    const startDate = `${selectedYear}-${selectedMonth}-01`;
    const lastDay = new Date(parseInt(selectedYear), parseInt(selectedMonth), 0).getDate();
    const endDate = `${selectedYear}-${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
    
    const filteredSales = allSales.filter(sale => {
      const saleDate = sale.sale_date?.split('T')[0];
      if (!saleDate || saleDate < startDate || saleDate > endDate) return false;
      
      // Temsilci filtresi
      if (selectedRep !== 'all' && sale.representative !== selectedRep) return false;
      
      // Ürün kontrolü - items veya product_name
      const items = sale.items || [];
      if (items.length > 0) {
        return items.some(item => item.product_name === product.product_name);
      }
      return sale.product_name === product.product_name;
    });
    
    setProductDetails(filteredSales);
    setShowDetailDialog(true);
  };
  
  // Excel Export
  const handleExport = () => {
    const exportData = sortedProducts.map(item => ({
      'Ürün Adı': item.product_name,
      'Toplam Adet': item.quantity,
      'Şirket Kârı (₺)': item.company_profit?.toFixed(2)
    }));
    
    // Toplam satırı ekle
    exportData.push({
      'Ürün Adı': 'TOPLAM',
      'Toplam Adet': totals.quantity,
      'Şirket Kârı (₺)': totals.company_profit?.toFixed(2)
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ürün Bazlı Rapor');
    
    const monthName = MONTHS.find(m => m.value === selectedMonth)?.label || selectedMonth;
    const repName = selectedRep === 'all' ? 'TumTemsilciler' : selectedRep;
    XLSX.writeFile(wb, `UrunBazliRapor_${monthName}_${selectedYear}_${repName}.xlsx`);
    toast.success('Excel dosyası indirildi');
  };
  
  // Sıralama ikonu render fonksiyonu
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'desc' 
      ? <ChevronDown className="w-4 h-4 inline ml-1" />
      : <ChevronUp className="w-4 h-4 inline ml-1" />;
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="product-based-reports-page">
      {/* Header */}
      <PageHeader
        icon={Package}
        title="Ürün Bazlı Raporlar"
        subtitle="Ürün bazında satış adetleri ve şirket kârı analizi"
      />
      
      {/* Filtre Alanı */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-end gap-4">
          {/* Ay Seçimi */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Ay
            </label>
            <Select value={selectedMonth} onValueChange={(val) => { setDateRangeMode('month'); setSelectedMonth(val); }} disabled={dateRangeMode !== 'month'}>
              <SelectTrigger className={`w-[140px] ${dateRangeMode !== 'month' ? 'opacity-50' : ''}`}>
                <SelectValue placeholder="Ay Seçin" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(month => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Yıl Seçimi */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Yıl</label>
            <Select value={selectedYear} onValueChange={(val) => { setDateRangeMode('month'); setSelectedYear(val); }} disabled={dateRangeMode !== 'month'}>
              <SelectTrigger className={`w-[100px] ${dateRangeMode !== 'month' ? 'opacity-50' : ''}`}>
                <SelectValue placeholder="Yıl" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Temsilci Seçimi */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
              <Users className="w-3 h-3" /> Temsilci
            </label>
            <Select value={selectedRep} onValueChange={setSelectedRep}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Temsilci Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.username}>
                    {u.full_name || u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Hızlı Filtreler */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleThisMonth}
              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              Bu Ay
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLastMonth}
              className={`${dateRangeMode === 'month' ? 'bg-gray-50 hover:bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'}`}
            >
              Geçen Ay
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLast3Months}
              className={`${dateRangeMode === 'last3months' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200'}`}
            >
              Son 3 Ay
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAllTime}
              className={`${dateRangeMode === 'alltime' ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-purple-50 hover:bg-purple-100 text-purple-600 border-purple-200'}`}
            >
              Tüm Zamanlar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
            >
              <X className="w-4 h-4 mr-1" />
              Temizle
            </Button>
          </div>
          
          {/* Excel Export */}
          <div className="ml-auto">
            <Button
              variant="outline"
              onClick={handleExport}
              className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
              disabled={products.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>
        
        {/* Seçili Filtre Özeti */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Dönem:</span> {
              dateRangeMode === 'last3months' ? 'Son 3 Ay' :
              dateRangeMode === 'alltime' ? 'Tüm Zamanlar' :
              `${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
            }
            {' | '}
            <span className="font-medium">Temsilci:</span> {selectedRep === 'all' ? 'Tüm Temsilciler' : users.find(u => u.username === selectedRep)?.full_name || selectedRep}
          </p>
        </div>
      </div>
      
      {/* Kategori Bazlı Özet Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Arvento */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-xs font-medium text-blue-100 mb-1">Arvento</div>
          <div className="text-2xl font-bold">{categoryTotals.arvento}</div>
          <div className="text-xs text-blue-200 mt-1">adet</div>
        </div>
        
        {/* Kamera */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-xs font-medium text-purple-100 mb-1">Kamera</div>
          <div className="text-2xl font-bold">{categoryTotals.kamera}</div>
          <div className="text-xs text-purple-200 mt-1">adet</div>
        </div>
        
        {/* M2M */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-xs font-medium text-orange-100 mb-1">M2M</div>
          <div className="text-2xl font-bold">{categoryTotals.m2m}</div>
          <div className="text-xs text-orange-200 mt-1">adet</div>
        </div>
        
        {/* Sensör */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-xs font-medium text-teal-100 mb-1">Sensör</div>
          <div className="text-2xl font-bold">{categoryTotals.sensor}</div>
          <div className="text-xs text-teal-200 mt-1">adet</div>
        </div>
        
        {/* Diğer */}
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl p-4 text-white shadow-lg">
          <div className="text-xs font-medium text-gray-100 mb-1">Diğer</div>
          <div className="text-2xl font-bold">{categoryTotals.diger}</div>
          <div className="text-xs text-gray-200 mt-1">adet</div>
        </div>
      </div>
      
      {/* Tablo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Seçili dönemde satış kaydı bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th 
                    className="text-left px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('product_name')}
                  >
                    Ürün Adı
                    {renderSortIcon('product_name')}
                  </th>
                  <th 
                    className="text-right px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('quantity')}
                  >
                    Toplam Adet
                    {renderSortIcon('quantity')}
                  </th>
                  <th 
                    className="text-right px-6 py-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company_profit')}
                  >
                    Şirket Kârı (₺)
                    {renderSortIcon('company_profit')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedProducts.map((item, index) => (
                  <tr 
                    key={index} 
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(item)}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {item.product_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 text-right font-semibold">
                      {item.quantity?.toLocaleString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 text-sm text-green-600 text-right font-semibold">
                      {item.company_profit?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Alt Toplam */}
              <tfoot className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                <tr>
                  <td className="px-6 py-4 text-sm font-bold">
                    TOPLAM ({products.length} Ürün)
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-bold">
                    {totals.quantity?.toLocaleString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-green-400">
                    {totals.company_profit?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* SATIŞ ADET RAPORLARI - YENİ KATEGORİLER TABLOSU */}
      {salesQuantityData && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-4 md:p-6 border-b bg-gradient-to-r from-teal-50 to-emerald-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-teal-600" />
                  Satış Adet Raporları (Detaylı Kategoriler)
                </h3>
                <p className="text-sm text-gray-500 mt-1">Arvento Peşin, Operatör, Taksitli, M2M, Sensörler, Kamera Ürünleri</p>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Kategori</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Toplam Adet</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Ürün Sayısı</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Detay</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(salesQuantityData.category_totals)
                  .filter(([_, total]) => total > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, total]) => {
                    const products = salesQuantityData.products_by_category?.[category] || [];
                    const isExpanded = expandedCategories[category];
                    const categoryColors = {
                      'Arvento Peşin': 'bg-blue-100 text-blue-800',
                      'Arvento Operatör': 'bg-green-100 text-green-800',
                      'Arvento Taksitli': 'bg-purple-100 text-purple-800',
                      'M2M': 'bg-yellow-100 text-yellow-800',
                      'Sensörler': 'bg-orange-100 text-orange-800',
                      'Kamera Ürünleri': 'bg-pink-100 text-pink-800',
                      'Diğer': 'bg-gray-100 text-gray-800'
                    };
                    
                    return (
                      <React.Fragment key={category}>
                        <tr 
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleCategory(category)}
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">{category}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${categoryColors[category] || 'bg-gray-100 text-gray-800'}`}>
                              {total}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{products.length} ürün</td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          </td>
                        </tr>
                        
                        {isExpanded && products.length > 0 && (
                          <tr>
                            <td colSpan="4" className="px-0 py-0">
                              <div className="bg-gray-50 px-6 py-3 border-b">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {products.map((product, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                                      <span className="truncate flex-1 mr-2">{product.product_name}</span>
                                      <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                                        {product.total_quantity}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                
                <tr className="bg-teal-50 font-bold border-t-2 border-teal-300">
                  <td className="px-4 py-3 text-gray-900">GENEL TOPLAM</td>
                  <td className="px-4 py-3 text-center">
                    <span className="bg-teal-600 text-white px-4 py-1 rounded-full text-lg font-bold">
                      {Object.values(salesQuantityData.category_totals).reduce((a, b) => a + b, 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">
                    {Object.values(salesQuantityData.products_by_category || {}).reduce((acc, arr) => acc + arr.length, 0)} ürün
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Detay Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {selectedProduct?.product_name} - Detay Kayıtları
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-4">
              {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear} dönemi - 
              {selectedRep === 'all' ? ' Tüm Temsilciler' : ` ${users.find(u => u.username === selectedRep)?.full_name || selectedRep}`}
            </p>
            
            {productDetails.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Kayıt bulunamadı</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2">Tarih</th>
                      <th className="text-left px-4 py-2">Müşteri</th>
                      <th className="text-left px-4 py-2">Temsilci</th>
                      <th className="text-right px-4 py-2">Adet</th>
                      <th className="text-right px-4 py-2">Şirket Kârı</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {productDetails.map((sale, idx) => {
                      // Items varsa, sadece seçili ürünü al
                      let quantity = sale.quantity || 1;
                      let profit = sale.company_profit || 0;
                      
                      if (sale.items?.length > 0) {
                        const matchingItem = sale.items.find(i => i.product_name === selectedProduct?.product_name);
                        if (matchingItem) {
                          quantity = matchingItem.quantity || 1;
                          profit = matchingItem.company_profit || 0;
                        }
                      }
                      
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            {sale.sale_date ? format(new Date(sale.sale_date), 'dd MMM yyyy', { locale: tr }) : '-'}
                          </td>
                          <td className="px-4 py-2">{sale.customer_name || '-'}</td>
                          <td className="px-4 py-2">{sale.representative || '-'}</td>
                          <td className="px-4 py-2 text-right">{quantity}</td>
                          <td className="px-4 py-2 text-right text-green-600">
                            {profit?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductBasedReports;
